import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Users, Clock, CalendarClock, TrendingUp, Building, Megaphone, Pin, ArrowRight, IndianRupee, Receipt, UserMinus, ExternalLink, ShieldAlert } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import PasswordHealthCard from '../components/PasswordHealthCard';
import OffboardingCountdownBanner from '../components/OffboardingCountdownBanner';
import MyAssetsWidget from '../components/MyAssetsWidget';
import MyTicketsWidget from '../components/MyTicketsWidget';

const Card = ({ children, className = '', ...rest }) => (
  <div className={`bg-white border border-slate-200 rounded-2xl shadow-sm ${className}`} {...rest}>{children}</div>
);

const Stat = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <Card className="p-6 hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <div className="font-heading text-4xl font-bold mt-2 text-slate-900">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </div>
    </div>
  </Card>
);

function fmtTime(iso) { if (!iso) return '—'; return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
function inr(n) { if (n == null) return '—'; return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`; }

/**
 * Renders the attendance trend chart with EXPLICIT width/height to avoid
 * Recharts' first-paint "width(-1) of chart should be greater than 0" warning
 * that ResponsiveContainer emits before the parent is measured.
 */
function AttendanceTrendChart({ data }) {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const cw = el.clientWidth || 0;
      if (cw > 0) setW(cw);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className="h-64 min-w-0 w-full">
      {w > 0 && (
        <LineChart width={w} height={256} data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey="date" stroke="#94A3B8" fontSize={11} tickFormatter={(d) => d.slice(5)} />
          <YAxis stroke="#94A3B8" fontSize={11} allowDecimals={false} />
          <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }} />
          <Line type="monotone" dataKey="present" stroke="#3D4A8E" strokeWidth={2.5} dot={{ r: 4, fill: '#3D4A8E' }} />
        </LineChart>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [balance, setBalance] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [activeResignations, setActiveResignations] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, a, b] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/announcements').catch(() => ({ data: [] })),
          api.get('/leave-balance/me').catch(() => ({ data: null })),
        ]);
        setStats(s.data);
        setAnnouncements((a.data || []).slice(0, 3));
        setBalance(b.data);
        // Load active/pending resignations for HR/admin
        if (s.data?.scope === 'org') {
          try {
            const [pending, active] = await Promise.all([
              api.get('/offboardings?status=pending'),
              api.get('/offboardings?status=active'),
            ]);
            const all = [...(pending.data || []), ...(active.data || [])]
              .sort((x, y) => (y.last_working_day || '').localeCompare(x.last_working_day || ''));
            setActiveResignations(all);
          } catch (e) { /* ignore */ }
        }
      } catch (e) { console.error(e); }
    })();
  }, []);

  const scope = stats?.scope;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">
            {scope === 'personal' ? 'Your space' : scope === 'team' ? 'Team control room' : 'Control room'}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">
            Welcome, {user?.name?.split(' ')[0]} 👋
          </h1>
          <p className="text-slate-500 mt-1">
            {scope === 'personal' ? "Here's your personal snapshot for today." :
             scope === 'team' ? "Here's how your team is doing today." :
             `Here's what's happening across ${user?.tenant?.company_name || 'Lanvaus'} today.`}
          </p>
        </div>
      </div>

      {/* Password health — visible to everyone */}
      <PasswordHealthCard />

      {/* 24-hour offboarding countdown — only shows for users whose terminated_at is within the last 24h */}
      <OffboardingCountdownBanner user={user} />

      {/* My company assets — auto-hides if none assigned */}
      <MyAssetsWidget />

      {/* Helpdesk tickets — role-scoped, auto-hides for inactive users */}
      <MyTicketsWidget />

      {/* PERSONAL view (employee) */}
      {scope === 'personal' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <Stat icon={Clock} label="My check-in"
              value={stats?.my_attendance_today?.check_in ? fmtTime(stats.my_attendance_today.check_in) : '—'}
              sub={stats?.my_attendance_today?.check_out ? `Out: ${fmtTime(stats.my_attendance_today.check_out)}` : (stats?.my_attendance_today?.check_in ? 'In office' : 'Not checked in')}
              accent="bg-emerald-50 text-emerald-600" testId="stat-my-attendance" />
            <Stat icon={CalendarClock} label="Pending leaves" value={stats?.my_pending_leaves ?? 0}
              sub="Awaiting approval" accent="bg-amber-50 text-amber-600" testId="stat-my-pending" />
            <Stat icon={IndianRupee} label="Latest payslip"
              value={stats?.my_latest_payslip ? inr(stats.my_latest_payslip.net) : '—'}
              sub={stats?.my_latest_payslip?.month || 'No payslip yet'}
              accent="bg-indigo-50 text-indigo-600" testId="stat-my-payslip" />
            <Stat icon={Receipt} label="Approved leaves" value={stats?.my_approved_leaves ?? 0}
              sub="This cycle" accent="bg-violet-50 text-violet-600" testId="stat-my-approved" />
          </div>

          {balance && (
            <Card className="p-6" data-testid="my-leave-balance">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">My leave balance · {balance.year}</div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1 mb-4">Your time off</h2>
              <div className="grid grid-cols-2 gap-5">
                <BalancePill label="Paid leaves" remaining={balance.remaining.paid} total={balance.entitlement.paid} used={balance.used.paid} color="text-sky-600 bg-sky-50" />
                <BalancePill label="Sick leaves" remaining={balance.remaining.sick} total={balance.entitlement.sick} used={balance.used.sick} color="text-rose-600 bg-rose-50" />
              </div>
            </Card>
          )}
        </>
      )}

      {/* TEAM view (RM) and ORG view (admin/HR) share the metrics grid */}
      {scope !== 'personal' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <Stat icon={TrendingUp} label="Attendance Today" value={`${stats?.attendance_pct ?? 0}%`}
            sub={`${stats?.present_today ?? 0} of ${stats?.team_size ?? stats?.total_employees ?? 0} present`}
            accent="bg-emerald-50 text-emerald-600" testId="stat-attendance" />
          <Stat icon={Users} label={scope === 'team' ? 'Team size' : 'Active Employees'}
            value={stats?.team_size ?? stats?.total_employees ?? 0}
            sub={scope === 'team' ? 'Your direct reports' : 'Across all departments'}
            accent="bg-indigo-50 text-indigo-600" testId="stat-employees" />
          <Stat icon={CalendarClock} label="Pending Leaves" value={stats?.pending_leaves ?? 0}
            sub="Need your review" accent="bg-amber-50 text-amber-600" testId="stat-pending-leaves" />
          {scope === 'org' ? (
            <Stat icon={Clock} label="BGC queue" value={stats?.pending_onboarding ?? 0}
              sub="Awaiting HR review" accent="bg-violet-50 text-violet-600" testId="stat-bgc-queue" />
          ) : (
            <Stat icon={Clock} label="Approved Leaves" value={stats?.approved_leaves ?? 0}
              sub="This cycle" accent="bg-violet-50 text-violet-600" testId="stat-approved-leaves" />
          )}
        </div>
      )}

      {scope !== 'personal' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Card className="lg:col-span-2 p-6" data-testid="attendance-trend-card">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Last 7 days</div>
            <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1 mb-4">Attendance trend</h2>
            <AttendanceTrendChart data={stats?.trend || []} />
          </Card>

          {scope === 'org' && (
            <Card className="p-6" data-testid="departments-card">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Headcount</div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1 mb-5">By department</h2>
              <div className="space-y-3">
                {(stats?.departments || []).map((d, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#3D4A8E]/10 text-[#3D4A8E] flex items-center justify-center">
                        <Building className="w-4 h-4" />
                      </div>
                      <div className="text-sm font-medium text-slate-700">{d.name}</div>
                    </div>
                    <div className="text-sm font-bold text-slate-900">{d.count}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {scope === 'team' && (
            <Card className="p-6" data-testid="team-cta">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Manager tools</div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1 mb-3">Manage your team</h2>
              <div className="space-y-2">
                <Link to="/my-team" className="block p-3 rounded-xl hover:bg-slate-50 text-sm flex items-center justify-between">
                  <span>My team snapshot</span><ArrowRight className="w-4 h-4 text-slate-400" />
                </Link>
                <Link to="/leaves" className="block p-3 rounded-xl hover:bg-slate-50 text-sm flex items-center justify-between">
                  <span>Approve leaves</span><ArrowRight className="w-4 h-4 text-slate-400" />
                </Link>
                <Link to="/reviews" className="block p-3 rounded-xl hover:bg-slate-50 text-sm flex items-center justify-between">
                  <span>Write reviews</span><ArrowRight className="w-4 h-4 text-slate-400" />
                </Link>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Active Resignations widget — HR/admin only */}
      {scope === 'org' && activeResignations.length > 0 && (
        <Card className="p-6" data-testid="active-resignations-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                <UserMinus className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Offboarding · in progress</div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">Active resignations</h2>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {(stats?.pending_resignations ?? 0) > 0 && (
                <span data-testid="pending-resignations-badge"
                      className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200 inline-flex items-center gap-1">
                  <ShieldAlert className="w-3 h-3" /> {stats.pending_resignations} awaiting approval
                </span>
              )}
              {(stats?.active_offboardings ?? 0) > 0 && (
                <span data-testid="active-offboardings-badge"
                      className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-blue-100 text-blue-700 border border-blue-200">
                  {stats.active_offboardings} in progress
                </span>
              )}
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {activeResignations.slice(0, 8).map(o => {
              const isPending = o.status === 'pending';
              const pct = o.progress?.pct ?? 0;
              return (
                <Link key={o.id} to={`/employees/${o.employee_id}`} data-testid={`resignation-row-${o.id}`}
                      className="flex items-center justify-between gap-3 py-3 hover:bg-slate-50 -mx-2 px-2 rounded-xl">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-slate-900 text-sm truncate">{o.employee_name}</div>
                      <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${isPending ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                        {isPending ? 'Pending approval' : (o.kind || 'in progress').replace('_', ' ')}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                      {o.last_working_day && <span>LWD: <b className="text-slate-700">{o.last_working_day}</b></span>}
                      {!isPending && (
                        <span>Progress: <b className="text-slate-700">{o.progress?.done ?? 0}/{o.progress?.total ?? 8}</b></span>
                      )}
                    </div>
                    {!isPending && (
                      <div className="h-1 bg-slate-100 rounded-full overflow-hidden mt-2">
                        <div className="h-full bg-[#3D4A8E] transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    )}
                  </div>
                  <ExternalLink className="w-4 h-4 text-slate-400 flex-shrink-0" />
                </Link>
              );
            })}
          </div>
          {activeResignations.length > 8 && (
            <div className="mt-3 text-xs text-slate-500 text-right">Showing 8 of {activeResignations.length}</div>
          )}
        </Card>
      )}

      {/* Announcements widget — shown for all roles */}
      {announcements.length > 0 && (
        <Card className="p-6" data-testid="dashboard-announcements">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Company feed</div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">Latest announcements</h2>
            </div>
            <Link to="/announcements" className="text-xs text-[#3D4A8E] hover:underline flex items-center gap-1">
              See all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-3">
            {announcements.map(a => (
              <Link key={a.id} to="/announcements" className="block p-4 rounded-xl border border-slate-100 hover:bg-slate-50">
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${a.priority === 'high' ? 'bg-red-50 text-red-600' : 'bg-[#3D4A8E]/10 text-[#3D4A8E]'}`}>
                    {a.is_pinned ? <Pin className="w-4 h-4" /> : <Megaphone className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 text-sm">{a.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{a.content}</div>
                    <div className="text-xs text-slate-400 mt-1">{a.posted_by_name} · {new Date(a.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

const BalancePill = ({ label, remaining, total, used, color }) => (
  <div className="bg-slate-50 rounded-xl p-5" data-testid={`balance-${label.toLowerCase().replace(/\s/g,'-')}`}>
    <div className={`text-xs font-bold uppercase tracking-[0.2em] inline-block px-2 py-1 rounded-full ${color}`}>{label}</div>
    <div className="font-heading text-3xl font-bold mt-3 text-slate-900">
      {remaining}<span className="text-base text-slate-400 font-normal"> / {total}</span>
    </div>
    <div className="text-xs text-slate-500 mt-1">{used} used this year</div>
  </div>
);
